import { useT } from "../../i18n/hooks.ts";
import type { SubBasinLayer } from "../../model/gisLayer.ts";
import { assignOutletAquiferInBulk } from "../../model/projectEntities.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { AquifersSection } from "./Step3Outlets.tsx";
import { TopologyAssignmentSection } from "../TopologyAssignmentSection.tsx";

/**
 * Step 4: aquifers — create them and, right below, which one each
 * sub-basin drains into (like Acuiferos.vb + AsigAcuif.vb in the
 * original), the same "create + assign" shape Step2Zones.tsx already had
 * and Step3Outlets.tsx now also has for outlet points. Used to be a
 * generic "Topology" step covering BOTH outlet and aquifer assignment
 * across the whole project (with outlet points and aquifers both created
 * earlier, in step 3) — split apart because "create this entity" and
 * "assign it to sub-basins" are really the same task in two motions, not
 * two different phases of the wizard.
 */
export function Step4Topology({
  project,
  gisLayer,
  onChange,
}: {
  project: EvalHidProject;
  gisLayer: SubBasinLayer | null;
  onChange: (project: EvalHidProject) => void;
}) {
  const t = useT();
  const aquiferName = (cod: number) =>
    cod === 0 ? "—" : (project.aquifers.find((a) => a.aquiferCode === cod)?.name ?? `#${cod}`);

  return (
    <div>
      <h3>4. {t("infoPopover.aquifers")}</h3>

      <AquifersSection project={project} onChange={onChange} />

      {project.aquifers.length > 0 && (
        <TopologyAssignmentSection
          title={t("step4.outletAquifer")}
          gisLayer={gisLayer}
          project={project}
          isAssigned={(c) => c.aquiferCode !== 0}
          valueLabel={(c) => aquiferName(c.aquiferCode)}
          options={project.aquifers.map((a) => ({
            value: a.aquiferCode,
            label: a.name,
          }))}
          buttonText={t("aquifersPanel.assignAquifer")}
          unassignValue={0}
          onAssign={(codes, value) => onChange(assignOutletAquiferInBulk(project, codes, value))}
        />
      )}
    </div>
  );
}
